import React, { FC, useMemo } from 'react';
import { MyTripT } from './MyTrip';
import StatusLayout from '@/features/myTrips/components/Trips/StatusLayout';
import { Button, Column, Row, Svg } from '@wunder/ui-components';
import { getYear, format, isFuture, isAfter } from 'date-fns';
import styles from '@/features/myTrips/assets/scss/components/Trips/StatusLayout.module.scss';
import { Trans } from '@lingui/react/macro';
import { i18n } from '@lingui/core';
import { useDateLocale } from '@/core/hooks/useDateLocale';
import type { Locale } from 'date-fns';
import { useTypedSelector } from '@/core/redux/hooks';
import { getVehicleCategoryAdditions } from '@/features/vehicles/redux/Vehicles.selector';
import { useModal } from '@/core/components/UI/modals/ModalProvider';
import ExtendBooking, { ExtendBookingT } from '@/features/booking/components/modals/ExtendBooking';
import { toZonedTime } from 'date-fns-tz';
import { getMyTimezoneId } from '@/core/utils/getMyTimezoneId';

export const formatDateAndTime = (
  date: string,
  locale?: Locale,
  timezone = getMyTimezoneId(),
): string => {
  const zonedDate = toZonedTime(new Date(date), timezone);
  const currentYear = getYear(new Date());
  const yearOfDate = getYear(zonedDate);

  return currentYear === yearOfDate
    ? format(zonedDate, 'dd LLL HH:mm', { locale })
    : format(zonedDate, 'PPP', { locale });
};

const MyTripStatus: FC<Pick<MyTripT, 'trip' | 'view'>> = ({ trip, view }) => {
  const city = trip.branch.contactInfo?.address.city;
  const bookingAtoB = trip.branch.id !== trip.endBranch?.id && trip.endBranch;
  const hasStartAndEndTime = trip.startTime && trip.endTime;
  const isCanceled = trip.state === 'CANCELED';
  const isEditable = trip.editableUntil ? isAfter(new Date(trip.editableUntil), new Date()) : false;
  const isExtendable =
    !isEditable &&
    (view === 'ACTIVE' || view === 'UPCOMING') &&
    trip.extendableUntil &&
    isFuture(new Date(trip.extendableUntil)) &&
    trip.state !== 'BOOKED';
  const locale = useDateLocale();
  const businessTrip = trip.rentalType === 'BUSINESS';
  const additions = useTypedSelector(getVehicleCategoryAdditions);
  const { showModal } = useModal();

  const additionsString = useMemo(() => {
    if (!trip?.additions) return null;

    return trip.additions
      .map((additionCode) => {
        const addition = additions?.find((a) => a.code === additionCode);
        // Use addition name if found, otherwise use the code (e.g., for entitled additions)
        return addition?.name || additionCode;
      })
      .filter(Boolean)
      .join(', ');
  }, [additions, trip]);

  const rentalType = businessTrip ? (
    <Trans id="myTrips.status.rentalType.business">Business</Trans>
  ) : (
    <Trans id="myTrips.status.rentalType.personal">Personal</Trans>
  );

  return (
    <>
      <StatusLayout
        icon="user"
        title={<Trans id="myTrips.status.title">Account</Trans>}
        content={rentalType}
      />
      {city && (
        <StatusLayout
          icon="location"
          title={<Trans id="myTrips.status.location">Location</Trans>}
          content={city}
        />
      )}
      {bookingAtoB && (
        <StatusLayout
          icon="pin"
          title={<Trans id="myTrips.status.startEndStation">Start/end station</Trans>}
          content={
            <Row alignItems="center" justify="space-between">
              <Column>
                {trip.branch.name}
                <Svg
                  icon="arrowRight"
                  color="icon-300"
                  height={12}
                  width={12}
                  className={styles['status__content-icon']}
                />
                {bookingAtoB?.name}
              </Column>
            </Row>
          }
        />
      )}
      {additionsString && (
        <StatusLayout
          icon="plugins"
          title={<Trans id="myTrips.status.extras">Extras</Trans>}
          content={additionsString}
        />
      )}
      {hasStartAndEndTime && (
        <StatusLayout
          icon="calendar"
          title={<Trans id="myTrips.status.startAndEndTime">Pick-up and return date</Trans>}
          content={
            <Row alignItems="center" justify="space-between">
              <Column>
                {formatDateAndTime(trip.startTime, locale, trip.branch.timeZoneId)}
                <Svg
                  icon="arrowRight"
                  color="icon-300"
                  height={12}
                  width={12}
                  className={styles['status__content-icon']}
                />
                {formatDateAndTime(trip.endTime, locale, trip.branch.timeZoneId)}
              </Column>
              {isExtendable && (
                <Button
                  size="small"
                  variation="secondary"
                  onClick={() =>
                    showModal<ExtendBookingT>(ExtendBooking, {
                      bookingId: trip.id,
                      branchId: trip.branch.id,
                    })
                  }
                >
                  <Trans id="myTrips.status.extendBooking">Extend booking</Trans>
                </Button>
              )}
            </Row>
          }
        />
      )}
      {isCanceled && (
        <StatusLayout
          icon="restricted"
          title={<Trans id="myTrips.status">Status</Trans>}
          content={<Trans id="myTrips.status.canceled">Canceled</Trans>}
        />
      )}
      {trip.rental?.distance != null && (
        <StatusLayout
          icon="trips"
          title={<Trans id="myTrips.distance.title">Distance</Trans>}
          content={i18n.number(trip.rental.distance, {
            style: 'unit',
            unit: 'kilometer',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            unitDisplay: 'short',
          })}
        />
      )}
      {businessTrip && (
        <StatusLayout
          icon="agenda"
          title={<Trans id="myTrips.businessTripReason.title">Reason for business trip</Trans>}
          content={trip.reason}
        />
      )}
    </>
  );
};

export default MyTripStatus;
